/** Dive logs, on the diver's computer: list the dives in a log file, and cut
 * out the one chosen, so only that dive is sent with a post.
 *
 * A log holds every dive, buddies' names, notes, dive sites and their
 * positions, and the dive computer's serial number. None of it leaves the
 * device: cutDive copies only what the API reads (services/dive_profiles.py),
 * element by element from the lists below, so anything not listed (now or in
 * a later version of a format) is left behind. The API reads the profile
 * from what's sent, and checks it again.
 *
 * Two formats: UDDF (Shearwater Cloud's and Subsurface's exports, and
 * others') and Subsurface's own .ssrf (or its XML export). The app's
 * lib/utils/dive_log.dart is a port of this file: change both together.
 */

export class DiveLogError extends Error {}

const NOT_A_LOG = "That file isn't a dive log we can read. Choose a UDDF file, or a Subsurface dive log (.ssrf).";

// What's copied from a UDDF dive. Each entry: the attributes kept, and the
// children kept (by name), each by the same rules. `text` keeps its text.
const T = { text: true };
const UDDF_DIVE = {
  children: {
    informationbeforedive: { children: { datetime: T, airtemperature: T } },
    tankdata: {
      children: { link: { attrs: ["ref"] }, tankpressurebegin: T, tankpressureend: T },
    },
    samples: {
      children: {
        waypoint: {
          children: {
            divetime: T, depth: T, temperature: T,
            tankpressure: { text: true, attrs: ["ref"] },
            switchmix: { attrs: ["ref"] },
          },
        },
      },
    },
    informationafterdive: {
      children: { greatestdepth: T, averagedepth: T, diveduration: T, lowesttemperature: T },
    },
  },
};
const UDDF_MIX = { attrs: ["id"], children: { o2: T, he: T } };

// What's copied from a Subsurface dive.
const SSRF_DIVE = {
  attrs: ["date", "time", "duration"],
  children: {
    cylinder: { attrs: ["o2", "he", "start", "end"] },
    divecomputer: {
      attrs: ["model"],
      children: {
        depth: { attrs: ["max", "mean"] },
        temperature: { attrs: ["water", "air"] },
        event: { attrs: ["time", "name", "cylinder", "o2", "he"], only: (el) => el.getAttribute("name") === "gaschange" },
        sample: { attrs: ["time", "depth", "temp", "pressure", "pressure0"] },
      },
    },
  },
};

/** Elements' children by local name, whatever their namespace (UDDF's
 * depends on its version; Subsurface's has none). */
function kids(el, name) {
  return Array.from(el?.children ?? []).filter((c) => c.localName === name);
}

function kid(el, name) {
  return kids(el, name)[0] ?? null;
}

function path(el, ...names) {
  return names.reduce((at, name) => kid(at, name), el);
}

function num(text) {
  const value = parseFloat(String(text ?? "").replace("%", " "));
  return Number.isFinite(value) ? value : null;
}

/** "YYYY-MM-DDTHH:MM:SS" from the start of a date-time, its offset dropped:
 * the time the diver saw, as the API keeps it. */
function localTime(text) {
  const match = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(String(text ?? "").trim());
  return match ? `${match[1]}T${match[2]}:${match[3]}:${match[4] ?? "00"}` : null;
}

/** Subsurface's "55:40 min" (or "1:02:03 min", or "40 min") in seconds. */
function ssDuration(text) {
  const parts = String(text ?? "").trim().split(/\s+/)[0]?.split(":").map(Number) ?? [];
  if (!parts.length || parts.some((p) => !Number.isFinite(p)) || parts.length > 3) return null;
  if (parts.length === 1) return parts[0] * 60;
  return parts.reduce((total, p) => total * 60 + p, 0);
}

/** Reads a log's text: its format, and its dives newest first, each
 * {index, startedAt, durationS, maxDepthM, position, notes} (any may be null
 * but index). position is the dive site's {latitude, longitude} as the log
 * records it, and notes the diver's notes on the dive: neither is ever in
 * what cutDive sends.
 * Throws DiveLogError with a message fit to show. */
export function readLog(text) {
  const doc = new window.DOMParser().parseFromString(String(text ?? ""), "application/xml");
  const root = doc.documentElement;
  if (!root || root.localName === "parsererror" || doc.getElementsByTagName("parsererror").length) {
    throw new DiveLogError(NOT_A_LOG);
  }
  let format;
  let elements;
  if (root.localName === "uddf") {
    format = "uddf";
    elements = kids(path(root, "profiledata"), "repetitiongroup").flatMap((group) => kids(group, "dive"));
  } else if (root.localName === "divelog" || root.localName === "dives") {
    format = "subsurface";
    elements = Array.from(root.getElementsByTagName("dive"));
  } else {
    throw new DiveLogError(NOT_A_LOG);
  }
  if (!elements.length) throw new DiveLogError("That file has no dives in it.");

  const dives = elements.map((el, index) => ({
    index, ...(format === "uddf" ? uddfSummary(el, root) : ssrfSummary(el, root)),
  }));
  // Newest first; dives without a date last, in file order.
  const order = [...dives].sort((a, b) => {
    if (a.startedAt && b.startedAt) return a.startedAt < b.startedAt ? 1 : a.startedAt > b.startedAt ? -1 : a.index - b.index;
    if (a.startedAt || b.startedAt) return a.startedAt ? -1 : 1;
    return a.index - b.index;
  });
  return { format, doc, elements, dives: order };
}

/** {latitude, longitude} if both are numbers in range and not 0,0 (which
 * logs write for "no position"), else null. */
function position(lat, lng) {
  if (lat == null || lng == null || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  if (lat === 0 && lng === 0) return null;
  return { latitude: lat, longitude: lng };
}

/** The position of the site a UDDF dive links to (divesite/site/geography). */
function uddfPosition(dive, root) {
  const refs = new Set(kids(kid(dive, "informationbeforedive"), "link").map((l) => l.getAttribute("ref")));
  const site = kids(kid(root, "divesite"), "site").find((s) => refs.has(s.getAttribute("id")));
  const geography = kid(site, "geography");
  return position(num(kid(geography, "latitude")?.textContent), num(kid(geography, "longitude")?.textContent));
}

/** The position of a Subsurface dive's site (divesites/site gps="lat lng"). */
function ssrfPosition(dive, root) {
  const id = dive.getAttribute("divesiteid");
  if (!id) return null;
  const site = Array.from(root.getElementsByTagName("site")).find((s) => s.getAttribute("uuid") === id);
  const [lat, lng] = String(site?.getAttribute("gps") ?? "").trim().split(/\s+/).map(num);
  return position(lat ?? null, lng ?? null);
}

// Shearwater Cloud fills a dive's notes with a template of headings ("Summary:",
// "Gas:"); a heading with nothing after it isn't a note.
const EMPTY_HEADING = /^[\p{L} ]+:$/u;

/** The diver's notes: UDDF's paragraphs, a blank line between them. */
function uddfNotes(dive) {
  const notes = kid(kid(dive, "informationafterdive"), "notes");
  const paras = kids(notes, "para");
  if (!paras.length) return notes?.textContent.trim() || null;
  const written = paras.map((p) => p.textContent.trim()).filter((p) => p && !EMPTY_HEADING.test(p));
  return written.length ? written.join("\n\n") : null;
}

function uddfSummary(dive, root) {
  const waypoints = kids(kid(dive, "samples"), "waypoint");
  const after = kid(dive, "informationafterdive");
  const depths = waypoints.map((w) => num(kid(w, "depth")?.textContent)).filter((d) => d != null);
  const times = waypoints.map((w) => num(kid(w, "divetime")?.textContent)).filter((t) => t != null);
  return {
    startedAt: localTime(path(dive, "informationbeforedive", "datetime")?.textContent),
    durationS: num(kid(after, "diveduration")?.textContent) ?? (times.length ? Math.max(...times) : null),
    maxDepthM: num(kid(after, "greatestdepth")?.textContent) ?? (depths.length ? Math.max(...depths) : null),
    position: uddfPosition(dive, root),
    notes: uddfNotes(dive),
  };
}

function ssrfSummary(dive, root) {
  const computer = kid(dive, "divecomputer");
  const date = dive.getAttribute("date");
  const time = dive.getAttribute("time");
  const depths = kids(computer, "sample").map((s) => num(s.getAttribute("depth"))).filter((d) => d != null);
  return {
    startedAt: date && time ? localTime(`${date}T${time}`) : null,
    durationS: ssDuration(dive.getAttribute("duration") ?? computer?.getAttribute("duration")),
    maxDepthM: num(kid(computer, "depth")?.getAttribute("max")) ?? (depths.length ? Math.max(...depths) : null),
    position: ssrfPosition(dive, root),
    notes: kid(dive, "notes")?.textContent.trim() || null,
  };
}

/** A copy of el keeping only what spec allows (see UDDF_DIVE). */
function copy(el, spec, doc) {
  const out = doc.createElementNS(el.namespaceURI, el.localName);
  for (const name of spec.attrs ?? []) {
    if (el.hasAttribute(name)) out.setAttribute(name, el.getAttribute(name));
  }
  if (spec.text) out.textContent = el.textContent.trim();
  for (const child of el.children) {
    const childSpec = spec.children?.[child.localName];
    if (childSpec && (!childSpec.only || childSpec.only(child))) out.appendChild(copy(child, childSpec, doc));
  }
  return out;
}

function serialize(doc) {
  return `<?xml version="1.0" encoding="utf-8"?>\n${new window.XMLSerializer().serializeToString(doc)}`;
}

/** The dive at `index` (as readLog numbers them) as a log of its own,
 * holding only what the API reads: {format, xml}, ready to send. */
export function cutDive(log, index) {
  const source = log.elements[index];
  if (!source) throw new DiveLogError("Choose a dive from the list.");
  return { format: log.format, xml: log.format === "uddf" ? cutUddf(log, source) : cutSsrf(source) };
}

function cutUddf(log, source) {
  const root = log.doc.documentElement;
  const ns = root.namespaceURI;
  const doc = document.implementation.createDocument(ns, "uddf", null);
  const out = doc.documentElement;
  out.setAttribute("version", root.getAttribute("version") ?? "3.2.0");
  const el = (name, parent = out) => parent.appendChild(doc.createElementNS(ns, name));

  // What wrote the file, and the dive computer's model: never its serial number.
  const software = path(root, "generator", "name")?.textContent.trim();
  if (software) el("name", el("generator")).textContent = software;
  const computer = path(root, "diver", "owner", "equipment", "divecomputer");
  const model = (kid(computer, "model") ?? kid(computer, "name"))?.textContent.trim();
  if (model) el("model", el("divecomputer", el("equipment", el("owner", el("diver"))))).textContent = model;

  // Only the gases this dive refers to.
  const dive = copy(source, UDDF_DIVE, doc);
  const used = new Set(Array.from(dive.getElementsByTagNameNS(ns, "*"))
    .filter((e) => e.localName === "link" || e.localName === "switchmix")
    .map((e) => e.getAttribute("ref")));
  const mixes = kids(kid(root, "gasdefinitions"), "mix").filter((mix) => used.has(mix.getAttribute("id")));
  if (mixes.length) {
    const gases = el("gasdefinitions");
    mixes.forEach((mix) => gases.appendChild(copy(mix, UDDF_MIX, doc)));
  }
  el("repetitiongroup", el("profiledata")).appendChild(dive);
  return serialize(doc);
}

function cutSsrf(source) {
  const doc = document.implementation.createDocument(null, "divelog", null);
  const out = doc.documentElement;
  out.setAttribute("program", "subsurface");
  out.setAttribute("version", "3");
  out.appendChild(doc.createElement("dives")).appendChild(copy(source, SSRF_DIVE, doc));
  return serialize(doc);
}

/** The dive on the given day ("YYYY-MM-DD"), else the newest: the one a post
 * form starts on. dives as readLog gives them (newest first). */
export function preselectedDive(dives, day = null) {
  if (day) {
    const onTheDay = dives.find((d) => d.startedAt?.startsWith(day));
    if (onTheDay) return onTheDay;
  }
  return dives[0] ?? null;
}

/** A log file's text, from a file input's File. */
export function readFileText(file) {
  if (typeof file.text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const reader = new window.FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new DiveLogError("That file couldn't be opened."));
    reader.readAsText(file);
  });
}
